import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Mail, Globe, Send, ArrowUpRight, Loader2, MapPin } from "lucide-react";
import { motion } from "framer-motion";
import { API, ASSETS } from "../lib/assets";
import { Overline, Reveal } from "../components/Section";

const interests = [
  { v: "fit2fin", t: "Fit2Fin" },
  { v: "cricstorylive", t: "CricStoryLive" },
  { v: "partnership", t: "Partnership" },
  { v: "careers", t: "Careers" },
  { v: "press", t: "Press / Media" },
  { v: "other", t: "Something else" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", company: "", subject: "", message: "", interest: "fit2fin",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error("Please share a few more details (min 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll get back to you soon!");
      setDone(true);
      setForm({ name: "", email: "", company: "", subject: "", message: "", interest: "fit2fin" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-slate-950 text-white">
      <Toaster theme="dark" position="top-center" richColors />

      <section className="relative pt-40 pb-16 overflow-hidden grain">
        <div className="absolute inset-0">
          <div className="absolute -top-32 left-1/2 -translate-x-1/2 h-96 w-[600px] rounded-full bg-teal-500/15 blur-[120px]" />
          <div className="absolute inset-0 grid-bg opacity-30" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 text-center">
          <Reveal><Overline>Get in touch</Overline></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-display font-black text-4xl md:text-6xl tracking-tighter leading-[0.98]">
              Let's build <span className="text-gradient-teal">something together.</span>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl mx-auto text-lg text-slate-300">
              Whether you're a partner, brand, talent or curious explorer — we'd love to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* INFO CARDS */}
          <div className="lg:col-span-4 space-y-4">
            <Reveal>
              <a
                href={`mailto:${ASSETS.contact.email}`}
                data-testid="contact-info-email"
                className="group block rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.05] transition"
              >
                <div className="h-11 w-11 rounded-xl bg-teal-400/10 border border-teal-400/20 grid place-items-center text-teal-300 mb-4">
                  <Mail className="h-5 w-5" />
                </div>
                <Overline>Email</Overline>
                <div className="mt-2 font-display text-xl font-bold flex items-center gap-2">
                  {ASSETS.contact.email}
                  <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100 transition" />
                </div>
                <p className="mt-2 text-sm text-slate-400">For all enquiries, partnerships & press.</p>
              </a>
            </Reveal>

            <Reveal delay={0.05}>
              <a
                href="https://www.pjsrs.com"
                data-testid="contact-info-website"
                className="group block rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:bg-white/[0.05] transition"
              >
                <div className="h-11 w-11 rounded-xl bg-teal-400/10 border border-teal-400/20 grid place-items-center text-teal-300 mb-4">
                  <Globe className="h-5 w-5" />
                </div>
                <Overline>Website</Overline>
                <div className="mt-2 font-display text-xl font-bold flex items-center gap-2">
                  {ASSETS.contact.domain}
                  <ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100 transition" />
                </div>
              </a>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <div className="h-11 w-11 rounded-xl bg-teal-400/10 border border-teal-400/20 grid place-items-center text-teal-300 mb-4">
                  <MapPin className="h-5 w-5" />
                </div>
                <Overline>Company</Overline>
                <div className="mt-2 font-display text-lg font-bold leading-tight">
                  PJSRS Infotech Private Limited
                </div>
                <p className="mt-2 text-sm text-slate-400">An AI-first digital products company.</p>
              </div>
            </Reveal>
          </div>

          {/* FORM */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900 p-8 md:p-10 overflow-hidden">
                <div className="absolute -top-32 -right-32 h-80 w-80 rounded-full bg-teal-500/15 blur-[100px]" />

                {done ? (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    data-testid="contact-success"
                    className="relative text-center py-12"
                  >
                    <div className="h-16 w-16 mx-auto rounded-2xl bg-teal-400/15 border border-teal-400/30 grid place-items-center text-teal-300">
                      <Send className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 font-display text-3xl font-black">Message received.</h3>
                    <p className="mt-3 text-slate-400 max-w-md mx-auto">
                      Thank you for reaching out to PJSRS Infotech Private Limited. Our team will get back to you shortly at
                      the email you provided.
                    </p>
                    <button
                      data-testid="contact-send-another"
                      onClick={() => setDone(false)}
                      className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-6 py-3 text-sm hover:bg-white/10"
                    >
                      Send another message
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={onSubmit} className="relative space-y-5" data-testid="contact-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field label="Full name" name="name" value={form.name} onChange={onChange} required data-testid="contact-input-name" />
                      <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required data-testid="contact-input-email" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <Field label="Company (optional)" name="company" value={form.company} onChange={onChange} data-testid="contact-input-company" />
                      <div>
                        <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-teal-400 mb-2">I'm interested in</label>
                        <select
                          name="interest"
                          value={form.interest}
                          onChange={onChange}
                          data-testid="contact-input-interest"
                          className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3.5 text-white outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30 transition"
                        >
                          {interests.map((i) => (
                            <option key={i.v} value={i.v} className="bg-slate-900">{i.t}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <Field label="Subject" name="subject" value={form.subject} onChange={onChange} required data-testid="contact-input-subject" />
                    <div>
                      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-teal-400 mb-2">Message</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={onChange}
                        rows={6}
                        required
                        data-testid="contact-input-message"
                        placeholder="Tell us about your project, idea or how we can help..."
                        className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3.5 text-white placeholder-slate-500 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30 transition resize-none"
                      />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                      <div className="text-xs text-slate-500">
                        We respect your privacy. Submissions are sent securely to <span className="text-teal-300">{ASSETS.contact.email}</span>.
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit-button"
                        className="inline-flex items-center gap-2 rounded-full bg-teal-400 text-slate-950 px-7 py-3.5 text-sm font-bold hover:bg-teal-300 transition-all shadow-[0_0_32px_rgba(45,212,191,0.35)] disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {submitting ? "Sending..." : "Send message"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, name, type = "text", value, onChange, required = false, ...rest }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-teal-400 mb-2">
        {label}{required && " *"}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3.5 text-white placeholder-slate-500 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-400/30 transition"
        {...rest}
      />
    </div>
  );
}
